import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    TextField,
    Paper,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import { ContractNAM } from '../../models/contract/ContractNAM';

export interface DropOffData {
    quantity_in: number;
    quantity_out: number;
    quantity_deducted?: number;
    receipt_file: File;
    sortation_ticket_file: File;
    net_quantity?: number;
    receipt_uri?: string;
    sortation_ticket_uri?: string;
}

interface ModalDropOffProps {
    isOpen: boolean;
    onDismiss: (data?: DropOffData | null, role?: string) => void;
    initialData?: DropOffData;
    contract?: ContractNAM
}


const extractFileName = (fileName: string) => {
    const match = fileName.match(/buyerOrganisation\/([^?]+)/);
    const result = match ? match[1] : null;
    return result
}

const ModalDropOff: React.FC<ModalDropOffProps> = ({ isOpen, onDismiss, initialData, contract }) => {

    const [quantityIn, setQuantityIn] = useState<number | ''>(initialData?.quantity_in || '');
    const [quantityOut, setQuantityOut] = useState<number | ''>(initialData?.quantity_out || '');
    const [quantityDeducted, setQuantityDeducted] = useState<number | ''>(initialData?.quantity_deducted || '');
    const [receiptFile, setReceiptFile] = useState<File | null>(initialData?.receipt_uri ? new File([], extractFileName(initialData?.receipt_uri) ?? '') : null);
    const [sortationTicketFile, setSortationTicketFile] = useState<File | null>(initialData?.sortation_ticket_uri ? new File([], extractFileName(initialData?.sortation_ticket_uri) ?? '') : null);
    const [error, setError] = useState<string | null>(null);
    const [imageError, setImageError] = useState<{ receipt?: boolean; sortation?: boolean }>({});

    const readOnly = useMemo(() => {
        return contract?.isDelivered() ?? false
    }, [contract])

    const receiptInputRef = useRef<HTMLInputElement>(null);
    const sortationTicketInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setQuantityIn(initialData?.quantity_in || '');
        setQuantityOut(initialData?.quantity_out || '');
        setQuantityDeducted(initialData?.quantity_deducted || '');
        setReceiptFile(initialData?.receipt_uri ? new File([], extractFileName(initialData?.receipt_uri) ?? '') : null);
        setSortationTicketFile(initialData?.sortation_ticket_uri ? new File([], extractFileName(initialData?.sortation_ticket_uri) ?? '') : null);
    }, [initialData])

    const handleReceiptFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('Receipt must be an image file');
                return;
            }
            setReceiptFile(file);
            setError(null);
        }
    };

    const handleSortationTicketFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('Sortation ticket must be an image file');
                return;
            }
            setSortationTicketFile(file);
            setError(null);
        }
    };

    const handleSave = () => {
        setError(null);

        if (!contract?.isDelivered() && (!quantityIn || !quantityOut)) {
            setError('Quantity In and Quantity Out are required');
            return;
        }

        if (receiptFile?.size === 0 || sortationTicketFile?.size === 0) {
            setError('Both receipt and sortation ticket photos are required');
            return;
        }

        onDismiss({
            quantity_in: quantityIn as number,
            quantity_out: quantityOut as number,
            quantity_deducted: quantityDeducted !== '' ? quantityDeducted as number : undefined,
            receipt_file: receiptFile!,
            sortation_ticket_file: sortationTicketFile!,
        }, 'confirm');
    };

    const handleClose = () => {
        onDismiss(null, 'cancel');
    };

    const isConfirmDisable = contract?.isDelivered() ?
        (receiptFile?.size === 0 || sortationTicketFile?.size === 0) :
        (!quantityIn || !quantityOut || receiptFile?.size === 0 || sortationTicketFile?.size === 0)

    return (
        <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Drop-Off Information</Typography>
                    <IconButton edge="end" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {readOnly ? 'Net Quantity (kg)' : 'Quantities (kg)'}
                        </Typography>
                        {readOnly ? (
                            <TextField
                                label="Net Quantity"
                                type="number"
                                value={initialData?.net_quantity || ''}
                                fullWidth
                                disabled
                                InputProps={{
                                    readOnly: true,
                                }}
                            />
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="Quantity In"
                                    type="number"
                                    value={quantityIn}
                                    onChange={(e) => setQuantityIn(e.target.value ? Number(e.target.value) : '')}
                                    fullWidth
                                    required
                                    inputProps={{ min: 0 }}
                                />
                                <TextField
                                    label="Quantity Out"
                                    type="number"
                                    value={quantityOut}
                                    onChange={(e) => setQuantityOut(e.target.value ? Number(e.target.value) : '')}
                                    fullWidth
                                    required
                                    inputProps={{ min: 0 }}
                                />
                                <TextField
                                    label="Quantity Deducted (Optional)"
                                    type="number"
                                    value={quantityDeducted}
                                    onChange={(e) => setQuantityDeducted(e.target.value ? Number(e.target.value) : '')}
                                    fullWidth
                                    inputProps={{ min: 0 }}
                                />
                            </Box>
                        )}
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Receipt Photo
                        </Typography>
                        <>
                            <input
                                ref={receiptInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleReceiptFileChange}
                                style={{ display: 'none' }}
                            />
                            <Paper
                                sx={{
                                    p: 2,
                                    bgcolor: 'background.default',
                                    border: '2px dashed',
                                    borderColor: receiptFile ? 'success.main' : 'divider',
                                    cursor: 'pointer',
                                }}
                                onClick={() => receiptInputRef.current?.click()}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {receiptFile ? (
                                        <>
                                            <ImageIcon color="success" />
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {receiptFile.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {(receiptFile.size / 1024).toFixed(2)} KB
                                                </Typography>
                                            </Box>
                                        </>
                                    ) : (
                                        <>
                                            <CloudUploadIcon color="action" />
                                            <Typography variant="body2" color="text.secondary">
                                                Click to upload receipt photo
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </Paper>
                        </>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Sortation Ticket Photo
                        </Typography>
                        <>
                            <input
                                ref={sortationTicketInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleSortationTicketFileChange}
                                style={{ display: 'none' }}
                            />
                            <Paper
                                sx={{
                                    p: 2,
                                    bgcolor: 'background.default',
                                    border: '2px dashed',
                                    borderColor: sortationTicketFile ? 'success.main' : 'divider',
                                    cursor: 'pointer',
                                }}
                                onClick={() => sortationTicketInputRef.current?.click()}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {sortationTicketFile ? (
                                        <>
                                            <ImageIcon color="success" />
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {sortationTicketFile.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {(sortationTicketFile.size / 1024).toFixed(2)} KB
                                                </Typography>
                                            </Box>
                                        </>
                                    ) : (
                                        <>
                                            <CloudUploadIcon color="action" />
                                            <Typography variant="body2" color="text.secondary">
                                                Click to upload sortation ticket photo
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </Paper>
                        </>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button variant="outlined" onClick={handleClose}>
                    {readOnly ? 'Close' : 'Cancel'}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={isConfirmDisable}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalDropOff;
